import { AD_SPECS } from '@/constants/ad-specs';
import type { AdUnitPlatform } from '@/services/data/AdUnitsService';

/**
 * Editing ONE variant of an already-written ad, in place.
 *
 * Founder, on the ad-copy card: "instead of just having a massive list of 20
 * different possible headlines on the ads, maybe we could just put the inline
 * action, where they can highlight text and then suggest editing right there."
 * Paging the list to three at a time answered the first half of that sentence.
 * This module answers the second: until now an `ad_units` row's copy was
 * READ-ONLY everywhere in the app, so a headline that was three characters too
 * long or named the wrong benefit could only be fixed by regenerating the whole
 * ad and losing the fourteen assets that were already right.
 *
 * Why a pure module rather than logic inside the card: `ad_units.copy` is a
 * provider-shaped `Json` column and each platform stores a different object.
 * A splice done at the render site would have to re-derive that shape on every
 * keystroke, in a component that already carries three platform branches. Here
 * the shape rules are stated once, are testable without a DOM, and the service
 * that persists them never has to know which platform it is holding.
 */

/**
 * The list-shaped copy fields — the ones that render as a `VariantGroup` and
 * therefore the ones the founder's complaint is actually about.
 *
 * LinkedIn is deliberately absent. Its three fields (`commentary`, `headline`,
 * `description`) are single strings rendered as a definition list, not a set of
 * interchangeable options; there is no "fifteen stacked on top of each other"
 * problem there to solve, and adding an editor to a surface nobody complained
 * about would be addition for its own sake.
 */
export type AdCopySlot = 'primaryTexts' | 'headlines' | 'descriptions';

interface SlotSpec {
  /** Which `AD_SPECS` field states this slot's ceiling. */
  hardMax: number | null;
  adviseMax: number | null;
  /** Sentence naming the ceiling, for a person who has just exceeded it. */
  limitLabel: string;
}

/**
 * Ceilings come from `AD_SPECS`, never from a number typed here.
 *
 * That file carries a `provenance` string per limit precisely so an unverified
 * number is visible at the call site. Meta's are `they-assert` and have no hard
 * maximum at all, so Meta primary text gets NO counter rather than a
 * confidently-wrong one — an invented ceiling on the busiest field would be the
 * "no hallucination" law failing in the smallest possible way.
 */
const SLOT_SPECS: {
  [P in AdUnitPlatform]: Partial<Record<AdCopySlot, SlotSpec>>;
} = {
  meta: {
    primaryTexts: {
      hardMax: AD_SPECS.meta.primaryText.hardMax,
      adviseMax: AD_SPECS.meta.primaryText.adviseMax,
      limitLabel: 'Meta states no maximum for primary text.',
    },
    headlines: {
      hardMax: AD_SPECS.meta.headline.hardMax,
      adviseMax: AD_SPECS.meta.headline.adviseMax,
      limitLabel: 'Meta advises 40 characters or fewer.',
    },
  },
  google_rsa: {
    headlines: {
      hardMax: AD_SPECS.google_rsa.headline.hardMax,
      adviseMax: AD_SPECS.google_rsa.headline.adviseMax,
      limitLabel: 'Google rejects a headline over 30 characters.',
    },
    descriptions: {
      hardMax: AD_SPECS.google_rsa.description.hardMax,
      adviseMax: AD_SPECS.google_rsa.description.adviseMax,
      limitLabel: 'Google rejects a description over 90 characters.',
    },
  },
  linkedin: {},
};

export interface AdCopyLimit {
  hardMax: number | null;
  adviseMax: number | null;
  limitLabel: string;
}

/**
 * The ceiling for one slot, or null when the platform states none.
 *
 * Null is a real answer and must render as "no counter", not as "0". The
 * distinction matters most on Meta primary text, the one field where a writer
 * genuinely has room and a fake ceiling would push them to cut good copy.
 */
export function adCopyLimit(platform: AdUnitPlatform, slot: AdCopySlot): AdCopyLimit | null {
  const spec = SLOT_SPECS[platform][slot];
  if (!spec) return null;
  if (spec.hardMax === null && spec.adviseMax === null) return null;
  return spec;
}

/** `over` is a FAIL the platform will reject; `near` is that platform's own advice. */
export type AdCopyLengthState = 'ok' | 'near' | 'over';

export function adCopyLengthState(length: number, limit: AdCopyLimit | null): AdCopyLengthState {
  if (!limit) return 'ok';
  if (limit.hardMax !== null && length > limit.hardMax) return 'over';
  if (limit.adviseMax !== null && length > limit.adviseMax) return 'over';
  const ceiling = limit.hardMax ?? limit.adviseMax;
  if (ceiling !== null && length >= ceiling * 0.9) return 'near';
  return 'ok';
}

/**
 * Replace the text of ONE entry, returning a new copy object of the same shape.
 *
 * 🔴 TAKES THE RAW STORED JSON, NEVER THE PARSED `AdUnitRecord['copy']`, and
 * the distinction is data loss, not style. `AdUnitsService.parseCopy` runs the
 * column through a zod object schema, and a non-`.strict()` zod object DROPS
 * every key it does not declare. Measured 2026-08-27 against production: 10
 * Meta rows store `offerClass` and `warnings` INSIDE `copy`, and neither key is
 * in `MetaAdUnitCopyReadSchema`, so parsing one and writing the result back
 * would delete both. `readWarnings()` falls back to `copy.warnings` precisely
 * for those historical rows — so the rows that would lose their advisories are
 * exactly the rows that have nowhere else to keep them. Round-tripping through
 * the read schema is silent data loss disguised as a text edit.
 *
 * Handed the raw object, the spread below genuinely does what it claims: every
 * key it has never heard of survives, because nothing ever removed them.
 *
 * Returns `null` — never a partially-built object — when the edit does not
 * apply: wrong platform for that slot, index out of range, or a payload that is
 * absent or not an object. A caller that cannot tell "nothing to do" from "here
 * is your object with a hole in it" is how a provider-shaped column gets a
 * malformed row written into it.
 */
export function applyVariantEdit(
  platform: AdUnitPlatform,
  rawCopy: unknown,
  slot: AdCopySlot,
  index: number,
  text: string,
): Record<string, unknown> | null {
  if (typeof rawCopy !== 'object' || rawCopy === null || Array.isArray(rawCopy)) return null;
  if (!SLOT_SPECS[platform][slot]) return null;

  const copy = rawCopy as Record<string, unknown>;
  const list = copy[slot];
  if (!Array.isArray(list)) return null;
  if (index < 0 || index >= list.length) return null;

  const entry = list[index];
  if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) return null;

  const nextList = list.slice();
  nextList[index] = { ...(entry as Record<string, unknown>), text };

  return { ...copy, [slot]: nextList };
}

/**
 * Whether an edit is worth persisting at all.
 *
 * Trimmed comparison, because a trailing space typed and then tabbed away from
 * is not an edit — and a write that reports success while changing nothing
 * teaches a reader to distrust the next one that really did save.
 */
export function isVariantEdited(original: string, next: string): boolean {
  return original.trim() !== next.trim();
}

/**
 * The floor every platform in this module agrees on.
 *
 * `ad-schemas.ts` sets `min(3)` on every text asset for Meta, LinkedIn and
 * Google alike, so an empty or two-character headline would fail the read
 * schema on the way back in and render the whole card unreadable. Blocking the
 * save is the honest move: the alternative is writing a row that the very next
 * page load refuses to parse.
 */
export const AD_COPY_MIN_LENGTH = 3;

export function isVariantSavable(text: string): boolean {
  return text.trim().length >= AD_COPY_MIN_LENGTH;
}
